import {
  forwardRef,
  type ChangeEvent,
  type ClipboardEvent,
  type FocusEvent,
  type KeyboardEvent,
} from "react";

import { platformProps } from "../../lib";
import { mergeMapped } from "./merge-mapped";
import type { TextInputProps } from "./types";

/** Ref type, resolved per platform like the component. */
export type TextInputRef = HTMLInputElement;

function keyboardTypeToInputType(keyboardType?: TextInputProps["keyboardType"]): string {
  switch (keyboardType) {
    case "email-address":
      return "email";
    case "phone-pad":
      return "tel";
    case "url":
      return "url";
    default:
      return "text";
  }
}

/**
 * TextInput - Web implementation
 *
 * Thin wrapper around <input> that exposes a unified cross-platform API.
 * Only props that need mapping are destructured; the rest pass through
 * directly, and what's mapped wins over them (see `mergeMapped`).
 */
export const TextInput = forwardRef<TextInputRef, TextInputProps>(
  (
    {
      onChangeText,
      onFocus,
      onBlur,
      onSubmitEditing,
      onKeyDown,
      onPasteText,
      selectTextOnFocus,
      disabled = false,
      invalid = false,
      secureTextEntry = false,
      keyboardType,
      returnKeyType: _returnKeyType, // Not mapped on web yet (HTML `enterKeyHint`)
      autoCapitalize = "none",
      autoCorrect,
      testID,
      accessibilityLabel,
      className,
      id,
      // Native-only (the phone's text size); taken out so it doesn't reach the DOM
      maxFontSizeMultiplier: _maxFontSizeMultiplier,
      ...rest
    },
    ref,
  ) => (
    <input
      ref={ref}
      {...mergeMapped(rest, {
        type: secureTextEntry ? "password" : keyboardTypeToInputType(keyboardType),
        // Numeric stays a text input: `type="number"` ignores `maxLength`, accepts
        // `e`/`+`/`-`, and arrow keys step the value, dropping leading zeros.
        inputMode:
          keyboardType === "numeric" || keyboardType === "number-pad" ? "numeric" : undefined,
        disabled,
        autoCapitalize,
        spellCheck: autoCorrect,
        autoCorrect: autoCorrect === false ? "off" : undefined,
        className,
        ...platformProps({ testID, id }),
        "aria-label": accessibilityLabel,
        "aria-invalid": invalid || undefined,
        onChange: (e: ChangeEvent<HTMLInputElement>) => onChangeText?.(e.target.value),
        onFocus:
          onFocus || selectTextOnFocus
            ? (e: FocusEvent<HTMLInputElement>) => {
                onFocus?.();
                if (selectTextOnFocus) e.currentTarget.select();
              }
            : undefined,
        onBlur: onBlur ? () => onBlur() : undefined,
        // The DOM event itself, typed as `{ key }`: a handler a parent Slot
        // composed into `onKeyDown` still gets `preventDefault` and the rest
        onKeyDown:
          onKeyDown || onSubmitEditing
            ? (e: KeyboardEvent<HTMLInputElement>) => {
                onKeyDown?.(e);
                if (e.key === "Enter") onSubmitEditing?.();
              }
            : undefined,
        onPaste: onPasteText
          ? (e: ClipboardEvent<HTMLInputElement>) => {
              e.preventDefault();
              onPasteText(e.clipboardData.getData("text"));
            }
          : undefined,
      })}
    />
  ),
);

TextInput.displayName = "TextInput";
